/**
 * What a run looks like while it is happening.
 *
 * One reducer, frames in, a shape the progress view renders. Deliberately small: this models a
 * scatter → map → reduce pipeline that either finishes or does not. Pauses, retries, human input
 * and historical replay are the builder's problem, not a demo's.
 */

import type { Frame } from './client.js'

export type Phase = 'idle' | 'running' | 'succeeded' | 'failed'

export interface Step {
  nodeId: string
  title: string
  status: 'running' | 'succeeded' | 'failed'
  error: string | null
  elapsed: number | null
  /** Set only for the iteration container: how far through its passes it is. */
  progress: { done: number; total: number } | null
}

export interface RunState {
  phase: Phase
  steps: Step[]
  /** The End node's declared outputs, once the run finishes. */
  outputs: Record<string, unknown> | null
  error: string | null
  elapsed: number | null
  /**
   * Frames this build did not recognise. Surfaced rather than dropped: a stream that has started
   * saying something new is worth knowing about before it matters.
   */
  unknownFrames: number
}

export const initialRunState: RunState = {
  phase: 'idle',
  steps: [],
  outputs: null,
  error: null,
  elapsed: null,
  unknownFrames: 0,
}

const upsert = (steps: Step[], nodeId: string, patch: Partial<Step> & { title?: string }): Step[] => {
  const at = steps.findIndex((s) => s.nodeId === nodeId)
  if (at < 0) {
    return [
      ...steps,
      {
        nodeId,
        title: patch.title ?? nodeId,
        status: patch.status ?? 'running',
        error: patch.error ?? null,
        elapsed: patch.elapsed ?? null,
        progress: patch.progress ?? null,
      },
    ]
  }
  const next = [...steps]
  next[at] = { ...next[at]!, ...patch }
  return next
}

export function runReducer(state: RunState, frame: Frame): RunState {
  switch (frame.event) {
    case 'workflow_started':
      return { ...initialRunState, phase: 'running' }

    case 'node_started':
      return { ...state, steps: upsert(state.steps, frame.nodeId, { title: frame.title, status: 'running' }) }

    case 'node_finished':
      return {
        ...state,
        steps: upsert(state.steps, frame.nodeId, {
          title: frame.title,
          status: frame.status === 'succeeded' ? 'succeeded' : 'failed',
          error: frame.error,
          elapsed: frame.elapsed,
        }),
      }

    case 'iteration_started':
      return {
        ...state,
        steps: upsert(state.steps, frame.nodeId, {
          title: frame.title,
          status: 'running',
          progress: { done: 0, total: frame.length },
        }),
      }

    case 'iteration_next': {
      /*
       * ⚠️ `index` is the pass ABOUT TO RUN, and it fires before the first one — so index 0 means
       * "none finished yet", not "one done". Reading it as a completed count reports 1 of 6 before
       * anything has happened and never reaches 6 of 6, because it does not fire after the last.
       */
      const step = state.steps.find((s) => s.nodeId === frame.nodeId)
      const total = step?.progress?.total ?? 0
      return { ...state, steps: upsert(state.steps, frame.nodeId, { progress: { done: frame.index, total } }) }
    }

    case 'iteration_completed': {
      const step = state.steps.find((s) => s.nodeId === frame.nodeId)
      const total = step?.progress?.total ?? 0
      return {
        ...state,
        steps: upsert(state.steps, frame.nodeId, {
          status: frame.status === 'succeeded' ? 'succeeded' : 'failed',
          // Only on completion is the count honest: `iteration_next` never announces the last pass.
          progress: { done: total, total },
        }),
      }
    }

    case 'workflow_finished':
      return {
        ...state,
        phase: frame.status === 'succeeded' ? 'succeeded' : 'failed',
        outputs: frame.outputs,
        error: frame.error,
        elapsed: frame.elapsed,
        /*
         * ⚠️ Sweep anything still marked running into the run's own verdict.
         *
         * A node that never got its `node_finished` — because the run failed elsewhere, or the
         * stream ended — would otherwise spin for ever in the UI, which reads as "still working" on
         * a run that is over.
         */
        steps: state.steps.map((s) =>
          s.status === 'running' ? { ...s, status: frame.status === 'succeeded' ? 'succeeded' : 'failed' } : s,
        ),
      }

    case 'unknown':
      return { ...state, unknownFrames: state.unknownFrames + 1 }
  }
}
